/**
 * order-risk.utils.js
 *
 * CANONICAL, dependency-free order risk / fraud assessment.
 *
 * This is the single source of truth for "does this order need attention".
 * It is intentionally pure (no imports, no DB, no env) so the EXACT same file
 * can be copied verbatim into:
 *   - admin            -> src/utils/orderRisk.js
 *   - Happy-POS        -> src/utils/orderRisk.js
 *   - happy-services-app -> app/helpers/orderRisk.js
 *
 * It evaluates two things:
 *   1. HIGH DISCOUNT     – is the discount abnormally large for this order size?
 *                          Dynamic ceiling: max(absolute floor, % of gross).
 *   2. FRAUD-LIKE SIGNALS – cash + big discount, missing/invalid phone/email,
 *                          missing/short delivery address, near-free orders,
 *                          unpaid-but-closed, zero-total, tip anomalies, etc.
 *
 * Returns a stable object (see assessOrderRisk) whose `signals[].code` values
 * are localized in each frontend via `order_risk_<code>` i18n keys. The util
 * itself only emits stable codes + an English fallback label.
 */

/* eslint-disable no-restricted-syntax */

export const ORDER_RISK_VERSION = 1;

// Risk levels, ordered. Index === severity rank.
export const RISK_LEVELS = ['none', 'low', 'medium', 'high', 'critical'];

export const RISK_COLORS = {
  none: '#16a34a', // green
  low: '#65a30d', // lime
  medium: '#d97706', // amber
  high: '#ea580c', // orange
  critical: '#dc2626', // red
};

// Tunable thresholds. All amounts are assumed to be in the order's own
// currency (HUF by default). Pass an override object as the 2nd arg of
// assessOrderRisk to adjust per-restaurant if ever needed.
export const RISK_DEFAULTS = {
  // Discount ceiling = max(baseAbsolute, gross * softPercent/100).
  softPercent: 15, // "usually 10-15%"
  hardPercent: 30, // clearly elevated
  criticalPercent: 50, // half off — almost certainly needs review
  baseAbsolute: 3000, // "usually around 1.000 - 3.000 HUF"
  almostFreePercent: 90, // ~free order
  // Fraud heuristics
  largeCashOrder: 50000, // a big cash order is inherently higher risk
  bigOrder: 30000,
  minAddressLength: 6,
};

const SIGNAL_CODES = [
  'discount_elevated',
  'discount_high',
  'discount_critical',
  'discount_almost_free',
  'cash_high_discount',
  'cash_large_order',
  'missing_phone',
  'invalid_phone',
  'missing_email',
  'missing_address',
  'incomplete_address',
  'no_items',
  'zero_total',
  'closed_unpaid',
  'tip_anomaly',
  'storno',
];
export { SIGNAL_CODES };

// English fallback labels (frontends localize by code via i18n).
const SIGNAL_LABELS = {
  discount_elevated: 'Elevated discount',
  discount_high: 'High discount',
  discount_critical: 'Very high discount',
  discount_almost_free: 'Order almost free',
  cash_high_discount: 'Cash payment with large discount',
  cash_large_order: 'Large cash order',
  missing_phone: 'Missing phone number',
  invalid_phone: 'Invalid phone number',
  missing_email: 'Missing email',
  missing_address: 'Missing delivery address',
  incomplete_address: 'Incomplete delivery address',
  no_items: 'No line items',
  zero_total: 'Zero total with items',
  closed_unpaid: 'Closed but not paid',
  tip_anomaly: 'Unusually large tip',
  storno: 'Voided / storno order',
};

function num(v) {
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return typeof n === 'number' && isFinite(n) ? n : 0;
}

function round(v, dp = 1) {
  const f = Math.pow(10, dp);
  return Math.round(num(v) * f) / f;
}

function rankOf(level) {
  const i = RISK_LEVELS.indexOf(level);
  return i < 0 ? 0 : i;
}

function maxLevel(a, b) {
  return rankOf(a) >= rankOf(b) ? a : b;
}

function isCashOrder(order) {
  const pt = String(order.paymentType || '').toLowerCase();
  if (pt === 'cash' || (pt === '' && String(order.paymentSubtype || '').toLowerCase() === 'cod')) return true;
  const types = Array.isArray(order.paymentTypes) ? order.paymentTypes : [];
  return types.some((t) => String(t && t.paymentType || '').toLowerCase() === 'cash');
}

// Third-party aggregators that own the customer relationship + delivery.
// We legitimately do NOT receive the customer's phone/email/address for these,
// so a "missing contact" is expected — never a fraud signal.
const MARKETPLACE_SOURCES = ['foodora', 'netpincer', 'netpincér', 'wolt', 'bolt', 'uber', 'ubereats'];

function isMarketplaceOrder(order) {
  const from = String(order.orderFrom || '').toLowerCase();
  return MARKETPLACE_SOURCES.includes(from);
}

function isDeliveryLike(order) {
  const type = String(order.orderType || '').toLowerCase();
  const dtype = String(order.deliveryType || '').toLowerCase();
  const from = String(order.orderFrom || '').toLowerCase();
  if (type === 'online') return true;
  if (dtype === 'delivery' || dtype === 'parcellocker') return true;
  return ['foodora', 'netpincer', 'wolt', 'shopify', 'woocommerce', 'webshop'].includes(from);
}

function pickPhone(order) {
  const ui = order.userInfo || {};
  const inv = order.invoiceData || {};
  return ui.phone || ui.mobilePhone || ui.phoneNumber || inv.phone || order.phone || '';
}

function pickEmail(order) {
  const ui = order.userInfo || {};
  const inv = order.invoiceData || {};
  return ui.email || inv.email || order.email || '';
}

function validPhone(phone) {
  const digits = String(phone || '').replace(/[^\d]/g, '');
  return digits.length >= 7;
}

function validEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email || ''));
}

function addressText(order) {
  const a = order.addressInfo || {};
  return [a.zip, a.city, a.address, a.street, a.number]
    .filter(Boolean)
    .map((x) => String(x).trim())
    .filter(Boolean)
    .join(' ')
    .trim();
}

function itemCountOf(order) {
  const items = Array.isArray(order.items) ? order.items : [];
  return items.reduce((s, it) => s + (num(it && it.amount) || 1), 0);
}

/**
 * Assess one order. Returns a stable, serializable risk object.
 * `order` may be a Mongoose lean doc or a plain redux/context order.
 */
export function assessOrderRisk(order, overrides) {
  const cfg = Object.assign({}, RISK_DEFAULTS, overrides || {});
  const empty = {
    version: ORDER_RISK_VERSION,
    score: 0,
    level: 'none',
    needsAttention: false,
    discount: { amount: 0, percent: 0, grossBeforeDiscount: 0, expectedCeiling: 0, isHigh: false, level: 'none' },
    fraud: { score: 0, level: 'none' },
    signals: [],
    color: RISK_COLORS.none,
  };
  if (!order || typeof order !== 'object') return empty;

  const orderSum = num(order.orderSum);

  // ---- Discount ----
  let discountAmount = num(order.discountAmount) || num(order.discountPrice)
    || num(order.discounts && order.discounts.amount);
  const discountPercentField = num(order.discounts && order.discounts.percentage);
  const itemDiscount = (Array.isArray(order.items) ? order.items : [])
    .reduce((s, it) => s + num(it && it.discountAmount), 0);

  let grossBefore = num(order.productsPrice) || num(order.originalSum);
  if (grossBefore <= 0) grossBefore = orderSum + Math.max(discountAmount, itemDiscount);

  if (!discountAmount && discountPercentField > 0 && grossBefore > 0) {
    discountAmount = Math.round((grossBefore * discountPercentField) / 100);
  }
  const totalDiscount = Math.max(discountAmount, itemDiscount);
  const discountPercent = grossBefore > 0 ? (totalDiscount / grossBefore) * 100 : 0;

  const expectedCeiling = Math.max(cfg.baseAbsolute, (grossBefore * cfg.softPercent) / 100);

  let discountLevel = 'none';
  if (totalDiscount > 0 && grossBefore > 0) {
    if (discountPercent >= cfg.criticalPercent || totalDiscount > expectedCeiling * 2.5) {
      discountLevel = 'critical';
    } else if (discountPercent >= cfg.hardPercent || totalDiscount > expectedCeiling * 1.5) {
      discountLevel = 'high';
    } else if (discountPercent > cfg.softPercent || totalDiscount > expectedCeiling) {
      discountLevel = 'medium';
    }
  }
  const isHighDiscount = rankOf(discountLevel) >= rankOf('medium');

  // ---- Signals + fraud score ----
  const signals = [];
  let score = 0;
  const add = (code, weight, value) => {
    score += weight;
    signals.push({
      code,
      severity: weight >= 30 ? 'critical' : weight >= 18 ? 'high' : weight >= 10 ? 'medium' : 'low',
      weight,
      label: SIGNAL_LABELS[code] || code,
      value: value === undefined ? null : value,
    });
  };

  // discount-driven signals
  if (discountLevel === 'critical') add('discount_critical', 40, round(discountPercent));
  else if (discountLevel === 'high') add('discount_high', 25, round(discountPercent));
  else if (discountLevel === 'medium') add('discount_elevated', 12, round(discountPercent));
  if (discountPercent >= cfg.almostFreePercent && grossBefore > 0) add('discount_almost_free', 35, round(discountPercent));

  // payment-driven
  const cash = isCashOrder(order);
  if (cash && isHighDiscount) add('cash_high_discount', 20, totalDiscount);
  if (cash && orderSum >= cfg.largeCashOrder) add('cash_large_order', 12, orderSum);

  // contact / delivery integrity — only for the restaurant's OWN delivery/online
  // orders. Marketplace orders (Wolt/Foodora/Netpincér) never carry customer
  // contact in our system, so checking them just produces noise on every order.
  if (isDeliveryLike(order) && !isMarketplaceOrder(order)) {
    const phone = pickPhone(order);
    const email = pickEmail(order);
    const addr = addressText(order);
    if (!phone) add('missing_phone', 15);
    else if (!validPhone(phone)) add('invalid_phone', 12);
    if (order.needInvoice && !validEmail(email)) add('missing_email', 8);
    if (!addr) add('missing_address', 18);
    else if (addr.length < cfg.minAddressLength) add('incomplete_address', 8);
  }

  // value / item sanity (only when the items array is actually loaded —
  // a missing/unselected items field must not be treated as "no items")
  const hasItemsArray = Array.isArray(order.items);
  const itemCount = itemCountOf(order);
  if (hasItemsArray && itemCount === 0 && orderSum > 0 && !order.storno) add('no_items', 10);
  if (orderSum <= 0 && itemCount > 0 && !order.storno) add('zero_total', 20);

  // lifecycle anomalies
  if (String(order.status) === 'closed' && order.isPayed === false && !order.storno) add('closed_unpaid', 15);
  if (order.storno) add('storno', 8);

  // tip anomaly
  const tip = num(order.tipAmount);
  if (tip > 0 && orderSum > 0 && tip > orderSum * 0.5) add('tip_anomaly', 10, tip);

  score = Math.min(100, score);

  let fraudLevel = 'none';
  if (score >= 75) fraudLevel = 'critical';
  else if (score >= 50) fraudLevel = 'high';
  else if (score >= 25) fraudLevel = 'medium';
  else if (score > 0) fraudLevel = 'low';

  const level = maxLevel(discountLevel, fraudLevel);
  // order needs attention if discount is high OR fraud score reached medium+
  const needsAttention = isHighDiscount || rankOf(fraudLevel) >= rankOf('medium');

  // keep highest-severity signals first
  signals.sort((a, b) => b.weight - a.weight);

  return {
    version: ORDER_RISK_VERSION,
    score,
    level,
    needsAttention,
    discount: {
      amount: round(totalDiscount, 0),
      percent: round(discountPercent, 1),
      grossBeforeDiscount: round(grossBefore, 0),
      expectedCeiling: round(expectedCeiling, 0),
      isHigh: isHighDiscount,
      level: discountLevel,
    },
    fraud: { score, level: fraudLevel },
    signals,
    color: RISK_COLORS[level] || RISK_COLORS.none,
  };
}

export default assessOrderRisk;

/**
 * getOrderRisk(order) – prefer a backend-attached `order.orderRisk` (already
 * computed server-side with the canonical util) and fall back to computing it
 * client-side when absent.
 */
export function getOrderRisk(order) {
  if (order && order.orderRisk && typeof order.orderRisk === 'object') {
    return order.orderRisk;
  }
  return assessOrderRisk(order);
}

/**
 * riskLevelColor(level) – maps a risk level to the warning accent color used by
 * the attention surfaces (medium/high/critical). Returns null for none/low.
 */
export function riskLevelColor(level) {
  switch (level) {
    case 'medium':
      return '#d97706';
    case 'high':
      return '#ea580c';
    case 'critical':
      return '#dc2626';
    default:
      return null;
  }
}
